import { useTranslation } from 'react-i18next'
import { useChatbotFlow } from '../chatbot.provider'
import { ConnectingNodeButton } from './connecting-node-button.component'
import { AddingNodeButton } from './adding-node-button.component'
import { NodeAddedActions } from './node-added-actions.component'
import { TNodeIntegerRangeVariable } from '../node.type'

type TChatbotNodeIntegerRangeResponseProps = {
  id: string
  showActions: boolean
  variable: TNodeIntegerRangeVariable
}

export function ChatbotNodeIntegerRangeResponse({
  id,
  showActions,
  variable,
}: TChatbotNodeIntegerRangeResponseProps) {
  const { t } = useTranslation()
  const { addNodeWithConditionalConnection, edges, nodes } = useChatbotFlow()
  const currentNode = nodes.find((n) => n.id === id)

  const handleAddConditionalNode = (conditionKey: string) => (e: React.MouseEvent) => {
    e.stopPropagation()
    addNodeWithConditionalConnection(id, conditionKey)
  }

  const ranges = variable.condition?.ranges || []

  return (
    <div className="c-flex c-gap-2 c-p-2 c-border-t c-border-gray-200">
      {ranges.map((range, index) => {
        const handleId = `range-${index}`
        const hasEdge = edges.some((edge) => edge.source === id && edge.sourceHandle === handleId)
        const isEdgeSelected = edges.some(
          (edge) => edge.selected && edge.source === id && edge.sourceHandle === handleId,
        )

        const lowerBound = !isNaN(range.lowerBound) ? range.lowerBound : '?'
        const upperBound = !isNaN(range.upperBound) ? range.upperBound : '?'
        const label = t('pages.configuration.followups.chatbot.node-editor.range-label', {
          from: lowerBound,
          to: upperBound,
        })

        return (
          <div
            key={index}
            className="c-border c-border-grey-500 c-rounded c-px-4 c-relative c-pt-3 c-pb-5 c-flex-1"
          >
            <div className="c-text-xs c-font-semibold c-text-center c-text-nowrap">{label}</div>
            <NodeAddedActions currentNode={currentNode} conditionKey={handleId} />
            <div className="c-absolute c-bottom-[-14px] c-left-0 c-right-0 c-flex c-justify-center c-gap-2">
              <ConnectingNodeButton
                handleId={handleId}
                isConnectable={!hasEdge}
                isConnected={hasEdge}
                isPartOfSelectedEdge={isEdgeSelected}
                showIcon={showActions}
              />
              {showActions && !hasEdge && <AddingNodeButton onClick={handleAddConditionalNode(handleId)} />}
            </div>
          </div>
        )
      })}
    </div>
  )
}
